<template>
  <section ref="root" :class="[$style['reel'], moving && $style['reel--moving']]" aria-labelledby="reel-title">
    <div ref="track" :class="$style['reel__track']">
      <div :class="$style['reel__intro']">
        <h2 id="reel-title" :class="$style['reel__title']">{{ title }}</h2>
        <p>{{ text }}</p>
      </div>
      <figure v-for="photo in photos" :key="photo.src" :class="$style['reel__card']">
        <NuxtImg :src="photo.src" :alt="photo.alt" width="600" height="900" format="webp" loading="lazy" />
        <figcaption>{{ photo.caption }}</figcaption>
      </figure>
    </div>
  </section>
</template>
<script setup lang="ts">
// You scroll down, the photos slide sideways past you. Without motion it is a row you can swipe.
defineProps<{
  title: string
  text: string
  photos: Array<{ src: string, alt: string, caption: string }>
}>()

const { prefersReducedMotion, register, scheduleRefresh } = useStageMotion()
const root = ref<HTMLElement>()
const track = ref<HTMLElement>()
const moving = ref(false)
let context: ReturnType<ReturnType<typeof register>['gsap']['context']> | undefined

onMounted(() => {
  if (prefersReducedMotion() || !track.value) return
  const { gsap } = register()
  moving.value = true
  const distance = () => Math.max(0, (track.value?.scrollWidth ?? 0) - window.innerWidth)
  context = gsap.context(() => {
    gsap.to(track.value!, {
      x: () => -distance(),
      ease: 'none',
      scrollTrigger: { trigger: root.value, start: 'top top', end: () => `+=${distance()}`, scrub: 0.5, pin: true, refreshPriority: 2, invalidateOnRefresh: true }
    })
  }, root.value)
  scheduleRefresh()
})

onBeforeUnmount(() => context?.revert())
</script>
<style module>
.reel {
  overflow-x: auto;
  scroll-snap-type: x proximity;
  background: var(--stage-black);
  color: var(--stage-white);
}

/* Moved by the scroll: no snapping, or the browser shifts the strip to the first photo before it starts */
.reel--moving {
  overflow: hidden;
  scroll-snap-type: none;
}

.reel__track {
  display: flex;
  align-items: center;
  gap: clamp(1.5rem, 3vw, 3.5rem);
  width: max-content;
  padding: clamp(5rem, 14vh, 9rem) var(--stage-gutter);
}

.reel__intro {
  flex: none;
  width: min(84vw, 44rem);
}

.reel__intro p {
  max-width: 28rem;
  margin: var(--spacing-xl) 0 0;
  color: var(--stage-soft);
}

.reel__title {
  margin: 0;
  font-family: var(--font-family-heading);
  font-weight: var(--font-weight-bold);
  font-size: clamp(3rem, 1.2rem + 7.5vw, 9.5rem);
  line-height: 0.88;
  letter-spacing: -0.035em;
  text-transform: uppercase;
  text-wrap: balance;
}

.reel__card {
  flex: none;
  width: clamp(15rem, 25vw, 25rem);
  margin: 0;
  scroll-snap-align: center;
}

.reel__card:nth-child(odd) {
  transform: rotate(2deg) translateY(2.5rem);
}

.reel__card:nth-child(even) {
  transform: rotate(-2deg) translateY(-2rem);
}

.reel__card img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 2 / 3;
  object-fit: cover;
}

.reel__card figcaption {
  margin-top: var(--spacing-m);
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--stage-soft);
}

@media (width <= 48rem) {
  .reel__card:nth-child(odd) {
    transform: rotate(2deg) translateY(1rem);
  }

  .reel__card:nth-child(even) {
    transform: rotate(-2deg) translateY(-1rem);
  }
}
</style>
